import React, { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { VIDEO_MIME_TYPE_TO_EXTENSION, MAX_VIDEO_DURATION } from "@rapid/constants/media";
import { IconFrame } from "@scripts/react/components/IconFrame";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@rapid/analytics/types";
import InlineAlert from "@scripts/react/components/InlineAlert";
import { useTestMediaSupport } from "@rapid/hooks/useTestMediaSupport";
import { useMobile } from "@rapid/hooks/useMobile";
import { MIN_INFERENCES_SAMPLE } from "@rapid/constants/inference";
import Button from "@scripts/react/components/Button";
import { useMedia } from "@rapid/contexts/MediaContext";
import { UploadLimitHint } from "@rapid/components/flow/build/Hints/UploadLimitHint";
import { UploadLimitErrorType } from "@rapid/constants/media";
import { InlineTutorialCard } from "@rapid/components/tutorial/TutorialCard";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { Flows, DeploySteps, BuildSteps } from "@rapid/types/flows";
import { preloadFFmpeg } from "@rapid/utils/videoEditing";
import { WebcamCaptureModal } from "@rapid/components/flow/shared/WebcamCaptureModal";

// Page type enum for cleaner component API
enum PageType {
    BUILD = "build",
    SHARE = "share",
    USE = "use",
    IMPROVE = "improve"
}

const UploadSection = ({
    isActiveAnimation,
    isDragging,
    fileDragError,
    fileInputError,
    showAllMethods,
    pageType,
    uploadLimitError,
    imagesOnly,
    onOpenWebcam
}: {
    isActiveAnimation: boolean;
    isDragging: boolean;
    fileDragError: string | string[] | null;
    fileInputError: Error[] | null;
    showAllMethods: boolean;
    pageType: PageType;
    uploadLimitError?: UploadLimitErrorType | null;
    imagesOnly?: boolean;
    onOpenWebcam?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
    const isSharePage = pageType === PageType.SHARE;
    const isUsePage = pageType === PageType.USE;
    const isImprovePage = pageType === PageType.IMPROVE;

    return (
        <div className={twClassNames("fileUploadMethod justify-center")}>
            {/* Don't show upload limit error on improve page since we allow more images */}
            {!showAllMethods && !isImprovePage && (
                <UploadLimitHint
                    uploadLimitError={uploadLimitError ?? null}
                    variant="inline"
                    hideIcon={isSharePage}
                />
            )}
            <div className="fileUploadMethodContent">
                <IconFrame
                    icon={isActiveAnimation ? "fad fa-arrow-up" : "far fa-arrow-up"}
                    size="lg"
                    color={isActiveAnimation ? "purple" : "gray"}
                    isCircle
                    className={twClassNames(
                        "transition duration-200 ease-out",
                        isActiveAnimation && "scale-110"
                    )}
                />
                {isDragging ? (
                    <h2 className="text-brand">Drop your files here</h2>
                ) : (
                    <div className="flex flex-col gap-2">
                        <h2
                            className={twClassNames(
                                "sm:text-xl",
                                isActiveAnimation ? "!text-brand" : "!text-primary",
                                isSharePage || isUsePage
                                    ? "whitespace-pre-line"
                                    : "whitespace-nowrap"
                            )}
                        >
                            {isSharePage
                                ? "Upload a file to try this model"
                                : isUsePage
                                  ? imagesOnly
                                      ? "Upload Images to Test"
                                      : "Upload a file to try this model"
                                  : isImprovePage
                                    ? "Upload images to improve this model"
                                    : showAllMethods
                                      ? "Upload images or videos"
                                      : `Upload an image or a short video`}
                        </h2>
                        {!imagesOnly && (
                            <div className="flex flex-col items-center gap-1 text-xs text-tertiary">
                                <p>Videos will be trimmed to {MAX_VIDEO_DURATION} seconds.</p>
                            </div>
                        )}
                        {!isDragging && !isImprovePage && (
                            <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
                                <Button type="secondary" size="small">
                                    <i className="far fa-folder-open" />
                                    Choose files
                                </Button>
                                {onOpenWebcam && (
                                    <Button type="secondary" size="small" onClick={onOpenWebcam}>
                                        <i className="far fa-webcam" />
                                        Use webcam
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {fileDragError && (
                <InlineAlert level="danger" type="primary" className="py-3 pr-3 text-left">
                    {fileDragError}
                </InlineAlert>
            )}
            {fileInputError && (
                <InlineAlert level="danger" type="primary" className="py-3 pr-3">
                    <div className="text-destructive">
                        {fileInputError.map((error) => (
                            <p key={error.message}>{error.message}</p>
                        ))}
                    </div>
                    <p>Please try again with a different file.</p>
                </InlineAlert>
            )}
        </div>
    );
};

function ShowAllMethodsFileInput({
    isActiveAnimation,
    isDragging,
    fileDragError,
    fileInputError,
    pageType,
    imagesOnly,
    onOpenWebcam
}: {
    isActiveAnimation: boolean;
    isDragging: boolean;
    fileDragError: string | string[] | null;
    fileInputError: Error[] | null;
    pageType: PageType;
    imagesOnly?: boolean;
    onOpenWebcam?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = useState<number | null>(null);

    useEffect(() => {
        if (containerRef.current && containerHeight === null) {
            setContainerHeight(containerRef.current.offsetHeight);
        }
    }, [containerHeight]);

    return (
        <div
            ref={containerRef}
            className="flex h-full w-full flex-col"
            style={{ height: containerHeight ? `${containerHeight}px` : "auto" }}
        >
            <div className="fileUploadContainer grid flex-1">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    key={isDragging ? "show-all-methods-dragging" : "show-all-methods-not-dragging"}
                    transition={{ duration: 0.15, ease: "easeInOut" }}
                    className="col-span-full"
                >
                    <UploadSection
                        isActiveAnimation={isActiveAnimation}
                        isDragging={isDragging}
                        fileDragError={fileDragError}
                        fileInputError={fileInputError}
                        showAllMethods={true}
                        pageType={pageType}
                        imagesOnly={imagesOnly}
                        onOpenWebcam={onOpenWebcam}
                    />
                </motion.div>
            </div>
        </div>
    );
}

function ShowOneMethodFileInput({
    isActiveAnimation,
    isDragging,
    fileDragError,
    fileInputError,
    pageType,
    uploadLimitError,
    imagesOnly,
    onOpenWebcam
}: {
    isActiveAnimation: boolean;
    isDragging: boolean;
    fileDragError: string | string[] | null;
    fileInputError: Error[] | null;
    pageType: PageType;
    uploadLimitError: UploadLimitErrorType | null;
    imagesOnly?: boolean;
    onOpenWebcam?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
    return (
        <>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                key={isDragging ? "show-one-method-dragging" : "show-one-method-not-dragging"}
                transition={{ duration: 0.15, ease: "easeInOut" }}
                className={twClassNames("fileUploadContainer hidden h-full sm:flex sm:pt-10")}
            >
                <UploadSection
                    isActiveAnimation={isActiveAnimation}
                    isDragging={isDragging}
                    fileDragError={fileDragError}
                    fileInputError={fileInputError}
                    showAllMethods={false}
                    pageType={pageType}
                    uploadLimitError={uploadLimitError}
                    imagesOnly={imagesOnly}
                    onOpenWebcam={onOpenWebcam}
                />
            </motion.div>
        </>
    );
}

type FileInputAreaProps = {
    addMediaItems: (files: File[]) => void;
    erroredMediaItems?: any[];
    showAllMethods?: boolean;
    externalIsHovered?: boolean;
    hasEverUploadedFiles?: boolean;
    uploadLimitError?: UploadLimitErrorType | null;
    imagesOnly?: boolean;
    className?: string;
};

export const FileInputArea = ({
    addMediaItems,
    erroredMediaItems = [],
    showAllMethods = false,
    externalIsHovered = false,
    hasEverUploadedFiles = false,
    uploadLimitError: uploadLimitErrorProp,
    imagesOnly,
    className
}: FileInputAreaProps) => {
    const { track } = useAnalytics();
    const { flow, step } = useFlowState();
    const { uploadLimitError: uploadLimitErrorFromContext } = useMedia();

    // Derive page type from flow state
    const pageType = useMemo(() => {
        if (flow === Flows.SHARE) return PageType.SHARE;
        if (step === DeploySteps.IMPROVE) return PageType.IMPROVE;
        if (
            step === DeploySteps.DEPLOY ||
            step === BuildSteps.PREVIEW ||
            step === DeploySteps.MODEL
        )
            return PageType.USE;
        return PageType.BUILD;
    }, [flow, step]);

    const isSharePage = pageType === PageType.SHARE;
    const isUsePage = pageType === PageType.USE;
    const isImprovePage = pageType === PageType.IMPROVE;
    const acceptImagesOnly = imagesOnly ?? isImprovePage;

    // Warm up FFmpeg when a file input area that accepts video renders,
    // so it's ready for trimming by the time a video is dropped.
    // preloadFFmpeg is idempotent — repeated calls return the same promise.
    useEffect(() => {
        if (!acceptImagesOnly) {
            preloadFFmpeg().catch(() => {});
        }
    }, [acceptImagesOnly]);

    // Use props if provided, otherwise fall back to context
    const uploadLimitError = uploadLimitErrorProp ?? uploadLimitErrorFromContext;
    const [isDragging, setIsDragging] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [fileInputError, setFileInputError] = useState<Error[] | null>(null);
    const [fileDragError, setFileDragError] = useState<string | string[] | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const uploadAreaRef = useRef<HTMLDivElement>(null);
    const { result: testedSupportedMimeTypes } = useTestMediaSupport();
    const isMobile = useMobile();
    const disableDueToMobile = isMobile && !showAllMethods;
    const [isWebcamModalOpen, setIsWebcamModalOpen] = useState(false);
    const isActiveAnimation =
        (isHovered || externalIsHovered || isDragging) && !disableDueToMobile && !isWebcamModalOpen;
    const supportsWebcamCapture =
        typeof navigator !== "undefined" &&
        Boolean(navigator.mediaDevices?.getUserMedia) &&
        !disableDueToMobile;

    // Since we now have backend video re-encoding, accept ALL video formats
    // Use browser-supported images + ALL video formats
    // TODO: Enable video upload in improve page
    const acceptedMimeTypes = useMemo(() => {
        const supportedImageMimeTypes = testedSupportedMimeTypes?.imageMimeTypesSupported || [];
        if (acceptImagesOnly) {
            return supportedImageMimeTypes;
        }
        const allVideoMimeTypes = Object.keys(VIDEO_MIME_TYPE_TO_EXTENSION);
        return [...supportedImageMimeTypes, ...allVideoMimeTypes];
    }, [testedSupportedMimeTypes, acceptImagesOnly]);

    const acceptedExtensions = useMemo(() => {
        const supportedImageExtensions = testedSupportedMimeTypes?.imageExtensionsSupported || [];
        if (acceptImagesOnly) {
            return supportedImageExtensions;
        }
        const allVideoExtensions = Object.values(VIDEO_MIME_TYPE_TO_EXTENSION).map(
            (info) => info.ext
        );
        // Use Set to deduplicate extensions (e.g., .avi appears multiple times)
        const uniqueExtensions = new Set([...supportedImageExtensions, ...allVideoExtensions]);
        return Array.from(uniqueExtensions);
    }, [testedSupportedMimeTypes, acceptImagesOnly]);

    // Memoized drag state reset function
    const resetDragState = useCallback(() => {
        setIsDragging(false);
        setIsHovered(false);
    }, []);

    const resetErrorState = useCallback(() => {
        setFileInputError(null);
        setFileDragError(null);
    }, []);

    // Class name helper
    const getUploadClasses = useCallback(() => {
        return twClassNames(
            // Base styles
            "flex w-full overflow-hidden text-center transition-all duration-100 ease-out group rounded-xl border-2 border-dashed border-gray-300",
            // Don't stretch height when showing inline tutorial card on mobile
            !disableDueToMobile && "flex-1 h-full",
            showAllMethods || hasEverUploadedFiles
                ? "items-stretch justify-between"
                : "items-center justify-center",
            isUsePage || isSharePage ? "bg-gray-50" : "bg-white",
            isActiveAnimation
                ? "border-brand bg-tint cursor-pointer"
                : (showAllMethods || hasEverUploadedFiles) && "bg-gray-50",
            showAllMethods || hasEverUploadedFiles
                ? isMobile
                    ? "h-full"
                    : "rounded-xl h-full"
                : isUsePage
                  ? "max-w-none ring-4 ring-inset ring-white"
                  : "max-w-screen-lg ring-4 ring-inset ring-white",
            className
        );
    }, [
        showAllMethods,
        isActiveAnimation,
        isMobile,
        hasEverUploadedFiles,
        disableDueToMobile,
        isUsePage,
        isSharePage,
        className
    ]);

    const checkDraggingValidFiles = useCallback(
        (e: DragEvent) => {
            if (e.dataTransfer && e.dataTransfer.items) {
                const items = Array.from(e.dataTransfer.items);
                const invalidItems = items.filter((item) => {
                    // Accept if MIME type matches or if MIME type is empty (browser doesn't recognize it)
                    // Empty MIME types are common for formats like .mkv, .avi, etc.
                    return item.type && !acceptedMimeTypes.includes(item.type);
                });

                if (invalidItems.length) {
                    const noValid = items.length === invalidItems.length;
                    setFileDragError([
                        `${noValid ? "The files" : "Some of the files"} you are trying to add are not supported and will be ignored.\nWe recommend trying one of the following formats: ${acceptedExtensions.join(", ")}`
                    ]);
                    if (noValid) {
                        e.dataTransfer.dropEffect = "none"; // prevent dropping
                    }
                } else {
                    setFileDragError(null);
                    e.dataTransfer.dropEffect = "copy";
                }
            }
        },
        [acceptedMimeTypes, acceptedExtensions]
    );

    const handleGlobalDrop = useCallback(
        (e: DragEvent) => {
            // Safari doesn't expose files/items during dragenter/over for security reasons
            // we check on drop specifically for safari to show an error
            checkDraggingValidFiles(e);
        },
        [checkDraggingValidFiles]
    );

    const handleGlobalDragOver = useCallback(
        (e: DragEvent) => {
            if (uploadAreaRef.current) {
                const rect = uploadAreaRef.current.getBoundingClientRect();
                const isInside =
                    e.clientX >= rect.left &&
                    e.clientX <= rect.right &&
                    e.clientY >= rect.top &&
                    e.clientY <= rect.bottom;

                if (isInside) {
                    // Set dragging state when inside upload area
                    setIsDragging(true);
                    // check if what we are dragging is valid or not
                    checkDraggingValidFiles(e);
                } else if (isDragging || isHovered) {
                    // Reset state when outside upload area
                    resetDragState();
                    resetErrorState();
                }
            }
        },
        [checkDraggingValidFiles, isDragging, isHovered, resetDragState, resetErrorState]
    );

    // Memoized visibility change handler
    const handleVisibilityChange = useCallback(() => {
        if (document.hidden && (isDragging || isHovered)) {
            resetDragState();
            resetErrorState();
        }
    }, [isDragging, isHovered, resetDragState, resetErrorState]);

    // Add effect to handle drag end when switching screens/apps
    useEffect(() => {
        // Only listen to events we actually need
        document.addEventListener("dragover", handleGlobalDragOver);
        document.addEventListener("drop", handleGlobalDrop);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("dragover", handleGlobalDragOver);
            document.removeEventListener("drop", handleGlobalDrop);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [handleGlobalDragOver, handleGlobalDrop, handleVisibilityChange]);

    // Handle file input errors
    useEffect(() => {
        if (erroredMediaItems.length) {
            setFileInputError(erroredMediaItems.map((item) => item.error));
        } else {
            setFileInputError(null);
        }
    }, [erroredMediaItems]);

    const handleFileUploadClick = useCallback(() => {
        // Disable upload on mobile screens
        if (disableDueToMobile) return;

        track({ event: AnalyticsFlowEvent.FILE_UPLOADER_CLICKED });
        fileInputRef.current?.click();
    }, [track, disableDueToMobile]);

    const handleFileInputClick = useCallback(
        (e: React.MouseEvent<HTMLInputElement>) => {
            e.stopPropagation(); // prevent parent drag and drop from triggering twice
            track({ event: AnalyticsFlowEvent.FILE_INPUT_CLICKED });
        },
        [track]
    );

    const handleOpenWebcam = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setIsWebcamModalOpen(true);
            track({ event: AnalyticsFlowEvent.CAMERA_INPUT_CLICKED, data: { source: "webcam" } });
        },
        [track]
    );

    const handleDragEvent = useCallback((e: React.DragEvent) => {
        // All drag state management handled by global listener
        e.preventDefault();
    }, []);

    const handleDrop = useCallback(
        async (e: React.DragEvent) => {
            e.preventDefault();
            if (disableDueToMobile) return;

            // Accept all supported files (browser-supported images + ALL video formats)
            // Also accept files with empty MIME types (browser doesn't recognize format)
            const files = Array.from(e.dataTransfer.files).filter((file) => {
                return !file.type || acceptedMimeTypes.includes(file.type);
            });

            if (files && files.length > 0) {
                addMediaItems(files);
            }

            resetDragState();
        },
        [addMediaItems, acceptedMimeTypes, resetDragState, disableDueToMobile]
    );

    const handleFileSelected = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            if (disableDueToMobile) return;

            const files = Array.from(e.target.files || []);
            if (files.length > 0) {
                addMediaItems(files);
            }
            // Reset the input value so the same file can be selected again
            e.target.value = "";
            resetErrorState();
        },
        [addMediaItems, resetErrorState, disableDueToMobile]
    );

    return (
        <div
            ref={uploadAreaRef}
            className={getUploadClasses()}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onDrop={handleDrop}
            onDragEnter={handleDragEvent}
            onDragOver={handleDragEvent}
            onDragLeave={handleDragEvent}
            onClick={handleFileUploadClick}
            data-testid="file-upload-area"
        >
            <input
                ref={fileInputRef}
                type="file"
                accept={acceptedMimeTypes.join(",")}
                multiple
                onChange={handleFileSelected}
                onClick={handleFileInputClick}
                className="hidden"
                data-testid="file-input"
            />
            {disableDueToMobile ? (
                <InlineTutorialCard />
            ) : showAllMethods ? (
                <ShowAllMethodsFileInput
                    isActiveAnimation={isActiveAnimation}
                    isDragging={isDragging}
                    fileDragError={fileDragError}
                    fileInputError={fileInputError}
                    pageType={pageType}
                    imagesOnly={acceptImagesOnly}
                    onOpenWebcam={supportsWebcamCapture ? handleOpenWebcam : undefined}
                />
            ) : (
                <ShowOneMethodFileInput
                    isActiveAnimation={isActiveAnimation}
                    isDragging={isDragging}
                    fileDragError={fileDragError}
                    fileInputError={fileInputError}
                    pageType={pageType}
                    uploadLimitError={uploadLimitError}
                    imagesOnly={acceptImagesOnly}
                    onOpenWebcam={supportsWebcamCapture ? handleOpenWebcam : undefined}
                />
            )}
            <WebcamCaptureModal
                isOpen={isWebcamModalOpen}
                onClose={() => setIsWebcamModalOpen(false)}
                onCapture={addMediaItems}
            />
        </div>
    );
};
